import { AppCard, AppCardBody, AppText, kindredFontFamilies } from "../../ui-foundation";
import {
  DocumentList,
  DocumentPageScaffold,
  DocumentSection,
} from "@/components/DocumentScaffold";
import {
  getKindredEditorialScopeBullet,
  getKindredEditorialSourceBullets,
  KINDRED_EDITORIAL_COUNTRY_COVERAGE_BULLETS,
  KINDRED_FEEDBACK_CHANNEL_PATH,
  KINDRED_PRIVACY_DATA_RIGHTS_LABEL,
  KINDRED_SUPPORT_COUNTRY_COVERAGE_COPY,
  KINDRED_SUPPORT_EMAIL,
  STEADY_HOUSE_LEGAL_NAME,
  STEADY_HOUSE_SUPPORT_URL,
} from "../../shared/support";

const EditorialPolicy = () => {
  return (
    <DocumentPageScaffold
      title="How Kindred Guidance Works"
      updatedLabel="Last updated: April 2026"
      fallbackPath="/"
      footerLinks={[
        { href: "/about-kindred", label: "About Kindred" },
        { href: "/privacy", label: "Privacy Policy" },
        { href: "/data-rights", label: KINDRED_PRIVACY_DATA_RIGHTS_LABEL },
        { href: "/conduct", label: "Community Guidelines" },
        { href: "/child-safety", label: "Child Safety" },
        { href: "/editorial-policy", label: "Editorial Policy" },
        { href: "/contact-complaints", label: "Contact & complaints" },
      ]}
    >
      <DocumentSection title="Why this page exists">
        <AppText>
          Kindred&apos;s Support Hub is designed to help single parents get oriented quickly, understand the
          main official routes, and feel less alone while dealing with practical life admin.
        </AppText>
        <AppText>{STEADY_HOUSE_LEGAL_NAME} is responsible for the editorial standards and correction process on this support content.</AppText>
        <AppText>
          It is written to be warm, plain-English, and genuinely useful. It is not designed to replace a
          solicitor, doctor, therapist, benefits adviser, social worker, or any other qualified
          professional.
        </AppText>
      </DocumentSection>

      <DocumentSection title="What Kindred support packs are">
        <DocumentList
          items={[
            "General information compiled from current public sources",
            "Step-by-step guidance to help you get started or get organised",
            "Links onward to official information and recognised support routes",
            getKindredEditorialScopeBullet(),
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Country coverage and limits">
        <DocumentList items={[...KINDRED_EDITORIAL_COUNTRY_COVERAGE_BULLETS]} />
        <AppText>{KINDRED_SUPPORT_COUNTRY_COVERAGE_COPY}</AppText>
      </DocumentSection>

      <DocumentSection title="What Kindred support packs are not">
        <DocumentList
          items={[
            "Legal advice",
            "Medical or mental health advice",
            "Financial advice or regulated financial guidance",
            "Safeguarding, crisis, or emergency decision-making support",
            "Personalised advice tailored to your exact circumstances",
          ]}
        />
        <AppText>
          If you need advice that depends on your specific situation, you should speak to a qualified
          professional or the relevant official service.
        </AppText>
      </DocumentSection>

      <DocumentSection title="How we write the packs">
        <AppText>
          We aim to build each pack from current official or widely recognised sources first, then
          translate that material into clearer, more usable steps.
        </AppText>
        <DocumentList items={getKindredEditorialSourceBullets()} />
      </DocumentSection>

      <DocumentSection title="Review cycle and updates">
        <AppText>
          Each support pack carries a review date and a planned review frequency. We review packs on a
          schedule, but rules, eligibility, deadlines, and local services can still change between
          reviews.
        </AppText>
        <AppText>That means the safest way to use a pack is:</AppText>
        <DocumentList
          items={[
            "use Kindred to understand the shape of the issue",
            "use the linked official sources to confirm the current details",
            "get professional help where your circumstances are complex, urgent, or disputed",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="When to get professional help">
        <AppText>Do not rely on a support pack alone if:</AppText>
        <DocumentList
          items={[
            "a child or adult may be at immediate risk",
            "you are facing a court deadline or formal legal dispute",
            "you need a diagnosis, treatment decision, or urgent mental health support",
            "you are making a major regulated financial decision",
            "you are being asked to sign an agreement you do not understand",
          ]}
        />
      </DocumentSection>

      <DocumentSection title="Corrections and feedback">
        <AppText>
          If you spot something that looks outdated, unclear, wrong, or too country-specific for the label it
          sits under, please tell us. We would rather review and correct guidance quickly than leave something
          ambiguous in place.
        </AppText>
        <AppText>
          Signed-in members can also share non-urgent product suggestions in{" "}
          <strong>{KINDRED_FEEDBACK_CHANNEL_PATH}</strong>, but corrections and complaints about guidance should
          come through the formal support route.
        </AppText>
        <AppText>
          Contact:{" "}
          <a
            href={STEADY_HOUSE_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kindred-inline-link"
          >
            Steady House support page
          </a>{" "}
          or{" "}
          <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
            {KINDRED_SUPPORT_EMAIL}
          </a>
        </AppText>
      </DocumentSection>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody alignItems="center">
          <AppText
            textAlign="center"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$md"
            lineHeight="$md"
          >
            Kindred aims to make good information easier to use, not to replace the people qualified to
            advise you, and not to blur one country's rules into another's.
          </AppText>
        </AppCardBody>
      </AppCard>
    </DocumentPageScaffold>
  );
};

export default EditorialPolicy;
